import {Component, OnDestroy, OnInit, computed, inject} from '@angular/core';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {CoreService} from '../../../core/services/core.service';
import {Subscription} from 'rxjs';
import {filter} from 'rxjs/operators';
import {CoreEventsService} from '../../../core/services/core-events.service';
import {Appointment, AppointmentType} from '@xmedical/core';
import {PatientAppointmentsStore, PatientStore} from '../../../core/state';

interface AppointmentYearGroup {
  year: number;
  appointments: Appointment[];
  isExpanded: boolean;
}

@Component({
    selector: 'app-patient-appointments-widget',
    templateUrl: './appointments-widget.component.html',
    styleUrls: ['./appointments-widget.component.scss'],
    standalone: false
})
export class AppointmentsWidgetComponent implements OnInit, OnDestroy {


  error = '';
  saving = false;
  appointmentTypes: AppointmentType[] = [];
  form: UntypedFormGroup = null;

  private patientStore = inject(PatientStore);
  protected appointmentsStore = inject(PatientAppointmentsStore);

  readonly appointments = computed(() => this.appointmentsStore.items());
  readonly loading = computed(() => this.appointmentsStore.loading());

  showGroupedView = computed(() => this.appointments().length > 10);

  appointmentsByYear = computed<AppointmentYearGroup[]>(() => {
    const appointments = this.appointments();
    if (appointments.length === 0) return [];

    const groupedByYear = new Map<number, Appointment[]>();
    for (const appointment of appointments) {
      const year = new Date(appointment.start).getFullYear();
      if (!groupedByYear.has(year)) groupedByYear.set(year, []);
      groupedByYear.get(year)!.push(appointment);
    }

    const years = Array.from(groupedByYear.keys()).sort((a, b) => b - a);
    return years.map((year, index) => ({
      year,
      appointments: groupedByYear.get(year)!,
      isExpanded: index === 0
    }));
  });

  private subscriptions: Subscription[] = [];

  constructor(
    private formBuilder: UntypedFormBuilder,
    private coreService: CoreService,
    private coreEventService: CoreEventsService
  ) { }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      appointmentDate: [null, Validators.required], // quirk to fix invalid date error on form. if value is null, date is valid, if value is empty string, its not a valid DATE so the field is marked invalid
      bookingComment: ['', Validators.maxLength(250)]
    });
    this.subscriptions.push(this.coreEventService.events$.pipe(filter(event => event.type === 'appointment_types_loaded')).subscribe(_ =>
      this.appointmentTypes = this.coreService.appointmentTypes().filter(t => t.isPatientAppointment).sort((a, b) => a.id > b.id ? 1 : -1)));
    this.appointmentTypes = this.coreService.appointmentTypes()?.filter(t => t.isPatientAppointment).sort((a, b) => a.id > b.id ? 1 : -1) ?? [];
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  get hasError(): boolean {
      return this.error.length > 0;
  }

  get hasAppointments(): boolean {
      return this.appointments().length > 0 && this.appointmentTypes.length > 0;
  }

  saveAppointment() {
    const patient = this.patientStore.current();
    if (!patient) return;
    this.saving = true;
    const field = this.form.get('appointmentDate') as UntypedFormControl;
    const date = field.value;
    const appointment = {
      title: patient.formalName,
      start: new Date(date),
      comment: '',
      practiceId: this.coreService.selectedPracticeId(),
      createdDate: new Date(),
      lastModifiedDate: new Date(),
      createdUser: this.coreService.currentUser,
      lastModifiedUser: this.coreService.currentUser,
      deleted: false,
      appointmentType: this.appointmentTypes[0]
    } as Appointment;
    this.error = '';
    this.subscriptions.push(this.appointmentsStore.add(appointment).subscribe({
      next: () => {
        field.setValue(null);
        this.saving = false;
      },
      error: () => {
        this.error = 'Could not save your new appointment';
        field.setValue(null);
        this.saving = false;
      }
    }));
  }

}
